<app-page-breadcrumb pageTitle="Solicitudes de acceso"></app-page-breadcrumb>

<div class="space-y-4">

  <!-- ── Barra superior ────────────────────────────────────────────────────── -->
  <div class="flex items-center justify-between gap-2 flex-wrap">
    <p class="text-sm text-gray-500 dark:text-gray-400">
      Personas que pidieron acceso desde la página pública. Revísalas e invítalas con un rol.
      @if (pendingCount > 0) {
        <span class="ml-1 inline-flex items-center rounded-full bg-brand-50 px-2 py-0.5 text-xs font-medium text-brand-600 dark:bg-brand-500/15 dark:text-brand-400">
          {{ pendingCount }} sin revisar
        </span>
      }
    </p>

    <div class="flex items-center gap-2">
      <select
        [(ngModel)]="statusFilter"
        (ngModelChange)="onFilterChange()"
        class="h-9 rounded-lg border border-gray-300 bg-transparent px-3 text-sm text-gray-700 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90">
        <option value="">Todos los estados</option>
        @for (s of STATUSES; track s) {
          <option [value]="s">{{ STATUS_LABEL[s] }}</option>
        }
      </select>

      <button type="button" (click)="load()" [disabled]="loading"
        class="flex items-center gap-1.5 rounded-lg bg-gray-100 px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-200 disabled:opacity-50 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700">
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
        Actualizar
      </button>
    </div>
  </div>

  @if (error) {
    <div class="rounded-lg border border-error-200 bg-error-50 px-4 py-3 text-sm text-error-600 dark:border-error-500/30 dark:bg-error-500/10 dark:text-error-400">
      {{ error }}
    </div>
  }

  <!-- ── Tabla ─────────────────────────────────────────────────────────────── -->
  <div class="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03]">
    <div class="overflow-x-auto">
      <table class="min-w-full text-sm">
        <thead class="border-b border-gray-200 bg-gray-50 dark:border-gray-800 dark:bg-gray-900/40">
          <tr class="text-left text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
            <th class="px-5 py-3">Empresa</th>
            <th class="px-5 py-3">Contacto</th>
            <th class="px-5 py-3">Teléfono</th>
            <th class="px-5 py-3">Estado</th>
            <th class="px-5 py-3">Recibida</th>
            <th class="px-5 py-3 text-right">Acciones</th>
          </tr>
        </thead>
        <tbody class="divide-y divide-gray-100 dark:divide-gray-800">
          @if (loading) {
            <tr>
              <td colspan="6" class="px-5 py-10 text-center text-gray-500 dark:text-gray-400">
                Cargando solicitudes…
              </td>
            </tr>
          } @else if (requests.length === 0) {
            <tr>
              <td colspan="6" class="px-5 py-12 text-center">
                <p class="font-medium text-gray-700 dark:text-gray-300">No hay solicitudes</p>
                <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Aparecerán aquí las que lleguen desde la página pública de acceso.
                </p>
              </td>
            </tr>
          } @else {
            @for (r of requests; track r.id) {
              <tr class="transition-colors hover:bg-gray-50 dark:hover:bg-white/[0.02]">
                <td class="px-5 py-4">
                  <p class="font-medium text-gray-800 dark:text-white/90">{{ r.companyName }}</p>
                  @if (r.message) {
                    <p class="mt-0.5 max-w-xs truncate text-xs text-gray-500 dark:text-gray-400">{{ r.message }}</p>
                  }
                </td>
                <td class="px-5 py-4">
                  <p class="text-gray-800 dark:text-white/90">{{ r.fullname }}</p>
                  <p class="text-xs text-gray-500 dark:text-gray-400">{{ r.email }}</p>
                </td>
                <td class="px-5 py-4 text-gray-600 dark:text-gray-400">{{ r.phone || '—' }}</td>
                <td class="px-5 py-4">
                  <span class="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium" [class]="statusClass(r.status)">
                    {{ STATUS_LABEL[r.status] }}
                  </span>
                </td>
                <td class="px-5 py-4 text-gray-600 dark:text-gray-400">{{ formatDate(r.createdAt) }}</td>
                <td class="px-5 py-4">
                  <div class="flex items-center justify-end gap-1.5">
                    <button type="button" (click)="openDetail(r)" title="Ver detalle"
                      class="rounded-lg p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    </button>

                    @if (r.status !== 'CONVERTED') {
                      <button type="button" (click)="openInvite(r)"
                        class="rounded-lg bg-brand-500 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-brand-600">
                        Invitar
                      </button>
                      <button type="button" (click)="openStatus(r, 'CONTACTED')" title="Marcar contactada"
                        class="rounded-lg p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200">
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 4v-4z" />
                        </svg>
                      </button>
                      @if (r.status !== 'REJECTED') {
                        <button type="button" (click)="reject(r)" title="Rechazar"
                          class="rounded-lg p-2 text-gray-500 transition-colors hover:bg-error-50 hover:text-error-600 dark:text-gray-400 dark:hover:bg-error-500/10 dark:hover:text-error-400">
                          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      }
                    }
                  </div>
                </td>
              </tr>
            }
          }
        </tbody>
      </table>
    </div>

    <!-- Paginación -->
    @if (!loading && requests.length > 0) {
      <div class="flex items-center justify-between gap-3 border-t border-gray-200 px-5 py-3 dark:border-gray-800">
        <div class="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
          <span>Por página</span>
          <select [(ngModel)]="pageSize" (ngModelChange)="onPageSizeChange()"
            class="h-8 rounded-lg border border-gray-300 bg-transparent px-2 text-sm text-gray-700 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90">
            @for (opt of PAGE_SIZE_OPTIONS; track opt) {
              <option [ngValue]="opt">{{ opt }}</option>
            }
          </select>
        </div>
        <div class="flex items-center gap-2">
          <button type="button" (click)="prevPage()" [disabled]="page === 0"
            class="rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-40 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800">
            Anterior
          </button>
          <span class="text-sm text-gray-500 dark:text-gray-400">Página {{ page + 1 }}</span>
          <button type="button" (click)="nextPage()" [disabled]="!hasNextPage"
            class="rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-40 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800">
            Siguiente
          </button>
        </div>
      </div>
    }
  </div>
</div>

<!-- ==================== Modal: detalle ==================== -->
<app-modal [isOpen]="!!detailRequest" (close)="closeDetail()" className="max-w-lg mx-4">
  @if (detailRequest; as r) {
    <div class="p-6 sm:p-8">
      <div class="mb-5">
        <h2 class="text-lg font-semibold text-gray-900 dark:text-white">{{ r.companyName }}</h2>
        <span class="mt-2 inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium" [class]="statusClass(r.status)">
          {{ STATUS_LABEL[r.status] }}
        </span>
      </div>

      <dl class="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <dt class="text-xs text-gray-500 dark:text-gray-400">Contacto</dt>
          <dd class="text-sm font-medium text-gray-800 dark:text-white/90">{{ r.fullname }}</dd>
        </div>
        <div>
          <dt class="text-xs text-gray-500 dark:text-gray-400">Correo</dt>
          <dd class="text-sm font-medium text-gray-800 dark:text-white/90">{{ r.email }}</dd>
        </div>
        <div>
          <dt class="text-xs text-gray-500 dark:text-gray-400">Teléfono</dt>
          <dd class="text-sm font-medium text-gray-800 dark:text-white/90">{{ r.phone || '—' }}</dd>
        </div>
        <div>
          <dt class="text-xs text-gray-500 dark:text-gray-400">Recibida</dt>
          <dd class="text-sm font-medium text-gray-800 dark:text-white/90">{{ formatDate(r.createdAt) }}</dd>
        </div>
      </dl>

      @if (r.message) {
        <div class="mt-5">
          <p class="text-xs text-gray-500 dark:text-gray-400">Mensaje</p>
          <p class="mt-1 whitespace-pre-line rounded-lg bg-gray-50 p-3 text-sm text-gray-700 dark:bg-gray-900/40 dark:text-gray-300">{{ r.message }}</p>
        </div>
      }

      @if (r.notes) {
        <div class="mt-5">
          <p class="text-xs text-gray-500 dark:text-gray-400">Notas internas</p>
          <p class="mt-1 whitespace-pre-line rounded-lg bg-gray-50 p-3 text-sm text-gray-700 dark:bg-gray-900/40 dark:text-gray-300">{{ r.notes }}</p>
        </div>
      }

      <div class="mt-7 flex justify-end gap-2">
        <button type="button" (click)="closeDetail()"
          class="rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800">
          Cerrar
        </button>
        @if (r.status !== 'CONVERTED') {
          <button type="button" (click)="closeDetail(); openInvite(r)"
            class="rounded-lg bg-brand-500 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-brand-600">
            Invitar
          </button>
        }
      </div>
    </div>
  }
</app-modal>

<!-- ==================== Modal: invitar con rol ==================== -->
<app-modal [isOpen]="!!inviteRequest" (close)="closeInvite()" className="max-w-md mx-4">
  @if (inviteRequest; as r) {
    <div class="p-6 sm:p-8">
      <div class="mb-5">
        <h2 class="text-lg font-semibold text-gray-900 dark:text-white">Invitar al CRM</h2>
        <p class="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
          Se enviará una invitación a
          <span class="font-medium text-gray-700 dark:text-gray-300">{{ r.email }}</span>
          para unirse a tu empresa.
        </p>
      </div>

      <div>
        <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
          Rol <span class="text-error-500">*</span>
        </label>
        <select [(ngModel)]="inviteRoleId" [disabled]="rolesLoading || inviteSaving"
          class="h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 disabled:opacity-50 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90">
          <option [ngValue]="null">{{ rolesLoading ? 'Cargando…' : '— Seleccionar rol —' }}</option>
          @for (role of roles; track role.id) {
            <option [ngValue]="role.id">{{ role.name }}</option>
          }
        </select>
        <p class="mt-2 text-xs text-gray-500 dark:text-gray-400">
          Solo puedes asignar roles iguales o inferiores al tuyo.
        </p>
      </div>

      <div class="mt-7 flex justify-end gap-2">
        <button type="button" (click)="closeInvite()" [disabled]="inviteSaving"
          class="rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800">
          Cancelar
        </button>
        <button type="button" (click)="confirmInvite()" [disabled]="inviteRoleId === null || inviteSaving"
          class="flex items-center gap-2 rounded-lg bg-brand-500 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-brand-600 disabled:opacity-50 disabled:cursor-not-allowed">
          @if (inviteSaving) {
            <svg class="h-4 w-4 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Enviando…
          } @else {
            Enviar invitación
          }
        </button>
      </div>
    </div>
  }
</app-modal>

<!-- ==================== Modal: cambiar estado ==================== -->
<app-modal [isOpen]="!!statusRequest" (close)="closeStatus()" className="max-w-md mx-4">
  @if (statusRequest; as r) {
    <div class="p-6 sm:p-8">
      <div class="mb-5">
        <h2 class="text-lg font-semibold text-gray-900 dark:text-white">Actualizar solicitud</h2>
        <p class="mt-0.5 text-sm text-gray-500 dark:text-gray-400">{{ r.companyName }} — {{ r.email }}</p>
      </div>

      <div class="space-y-4">
        <div>
          <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Estado</label>
          <select [(ngModel)]="statusValue" [disabled]="statusSaving"
            class="h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 disabled:opacity-50 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90">
            @for (s of STATUSES; track s) {
              <option [value]="s">{{ STATUS_LABEL[s] }}</option>
            }
          </select>
        </div>

        <div>
          <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Nota interna</label>
          <textarea [(ngModel)]="statusNotes" [disabled]="statusSaving" rows="3"
            placeholder="Ej. Reunión agendada para el jueves"
            class="w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 disabled:opacity-50 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30"></textarea>
          <p class="mt-1.5 text-xs text-gray-500 dark:text-gray-400">Se añade al historial, no reemplaza las notas anteriores.</p>
        </div>
      </div>

      <div class="mt-7 flex justify-end gap-2">
        <button type="button" (click)="closeStatus()" [disabled]="statusSaving"
          class="rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800">
          Cancelar
        </button>
        <button type="button" (click)="confirmStatus()" [disabled]="statusSaving"
          class="rounded-lg bg-brand-500 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-brand-600 disabled:opacity-50">
          {{ statusSaving ? 'Guardando…' : 'Guardar' }}
        </button>
      </div>
    </div>
  }
</app-modal>
